import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import Button from '../components/Button';

interface Assignment {
  id: string;
  title: string;
  description: string;
  access_code: string;
  created_at: string;
  created_by: string;
}

interface Submission {
  id: string;
  user_id: string;
  submitted_at: string;
}

const AssignmentView: React.FC = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [isCreator, setIsCreator] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAssignmentData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch assignment details
        const { data: assignmentData, error: assignmentError } = await supabase
          .from('assignments')
          .select('*')
          .eq('id', assignmentId)
          .single();
        
        if (assignmentError) throw assignmentError;
        
        if (!assignmentData) {
          setError('Assignment not found');
          setLoading(false);
          return;
        }
        
        setAssignment(assignmentData);
        setIsCreator(assignmentData.created_by === user?.id);
        
        // Fetch submissions if user is the creator
        if (assignmentData.created_by === user?.id) {
          const { data: submissionsData, error: submissionsError } = await supabase
            .from('submissions')
            .select('*')
            .eq('assignment_id', assignmentId);
          
          if (submissionsError) throw submissionsError;
          
          setSubmissions(submissionsData || []);
        } else {
          // Fetch user's own submissions
          const { data: userSubmissions, error: userSubmissionsError } = await supabase
            .from('submissions')
            .select('*')
            .eq('assignment_id', assignmentId)
            .eq('user_id', user?.id);
          
          if (userSubmissionsError) throw userSubmissionsError;
          
          setSubmissions(userSubmissions || []);
        }
        
      } catch (error: any) {
        console.error('Error fetching assignment data:', error);
        setError(error.message || 'Failed to load assignment');
      } finally {
        setLoading(false);
      }
    };

    if (assignmentId && user) {
      fetchAssignmentData();
    }
  }, [assignmentId, user]);

  const handleCreateSubmission = () => {
    navigate(`/assignment/${assignmentId}/submit`);
  };

  const handleViewSubmission = (submissionId: string) => {
    navigate(`/submission/${submissionId}`);
  };

  const handleEditAssignment = () => {
    navigate(`/assignment/${assignmentId}/edit`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !assignment) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error || 'Assignment not found'}
        </div>
        <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-primary mb-2">{assignment.title}</h1>
            <p className="text-gray-500 mb-4">
              Created on {new Date(assignment.created_at).toLocaleDateString()}
            </p>
          </div>
          
          <div>
            {isCreator ? (
              <Button onClick={handleEditAssignment}>Edit Assignment</Button>
            ) : (
              <Button onClick={handleCreateSubmission}>
                {submissions.length > 0 ? 'Submit Again' : 'Submit Assignment'}
              </Button>
            )}
          </div>
        </div>
        
        {isCreator && (
          <div className="bg-gray-100 px-4 py-2 rounded-md mb-6">
            <p className="text-sm font-medium">Access Code: <span className="font-bold">{assignment.access_code}</span></p>
            <p className="text-xs text-gray-600">Share this code with students to allow them to join this assignment</p>
          </div>
        )}
        
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">Description</h2>
          <div className="prose max-w-none">
            {assignment.description ? (
              <p>{assignment.description}</p>
            ) : (
              <p className="text-gray-500 italic">No description provided</p>
            )}
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">
            {isCreator ? 'Submissions' : 'Your Submissions'}
          </h2>
          
          {submissions.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">
                {isCreator
                  ? 'No submissions yet'
                  : 'You have not submitted any work for this assignment yet'}
              </p>
              {!isCreator && (
                <Button onClick={handleCreateSubmission}>Submit Assignment</Button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {submissions.map((submission) => (
                <div
                  key={submission.id}
                  className="py-4 flex justify-between items-center"
                >
                  <div>
                    <p className="font-medium">
                      Submitted on {new Date(submission.submitted_at).toLocaleString()}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => handleViewSubmission(submission.id)}
                  >
                    View Submission
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <div className="flex justify-center">
        <Button variant="outline" onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </Button>
      </div>
    </div>
  );
};

export default AssignmentView;
